export const CONSUMER_STAGES = ['Your care', 'Coverage', 'Your plan', 'Review'] as const;

/**
 * Every screen of the guided flow, in order, with its stage and the short label above its
 * heading. Steps marked `resumable` are saved as the consumer's place in the flow.
 */
export const CONSUMER_STEPS = {
  setting: { stage: 0, kicker: 'Choose where care happens', resumable: true },
  household: { stage: 0, kicker: 'Confirm who to cover', resumable: true },
  scenario: { stage: 0, kicker: 'Your care scenario', resumable: true },
  'payment-preference': { stage: 1, kicker: 'How you’d like to pay', resumable: true },
  searching: { stage: 1, kicker: 'Find coverage', resumable: false },
  options: { stage: 1, kicker: 'Choose one option', resumable: true },
  coverage: { stage: 1, kicker: 'Review this coverage', resumable: true },
  'policy-terms': { stage: 1, kicker: 'Policy terms', resumable: false },
  compare: { stage: 1, kicker: 'Compare policy terms', resumable: false },
  'premium-range': { stage: 1, kicker: 'Request a premium range', resumable: false },
  'existing-policy': { stage: 1, kicker: 'Review a policy you have', resumable: true },
  'review-upload': { stage: 1, kicker: 'Review a policy you have', resumable: false },
  'review-checkout': { stage: 1, kicker: 'Review a policy you have', resumable: false },
  'review-status': { stage: 1, kicker: 'Review a policy you have', resumable: true },
  'self-fund': { stage: 1, kicker: 'Self-fund your care', resumable: false },
  'self-fund-checkout': { stage: 1, kicker: 'Self-fund your care', resumable: false },
  assets: { stage: 1, kicker: 'Self-fund your care', resumable: true },
  'assessment-intro': { stage: 1, kicker: 'Self-fund your care', resumable: false },
  'assessment-accounts': { stage: 1, kicker: 'Self-fund your care', resumable: true },
  'assessment-projection': { stage: 1, kicker: 'Self-fund your care', resumable: true },
  walkthrough: { stage: 2, kicker: 'Walk through care', resumable: false },
  'walkthrough-summary': { stage: 2, kicker: 'See the whole care event', resumable: true },
  breakdown: { stage: 2, kicker: 'Care cost breakdown', resumable: false },
  'no-care': { stage: 2, kicker: 'If you never need care', resumable: false },
  medicaid: { stage: 2, kicker: 'If the rest feels out of reach', resumable: false },
  review: { stage: 3, kicker: 'Review your plan', resumable: true },
  change: { stage: 3, kicker: 'Change your plan', resumable: false },
  'application-start': { stage: 3, kicker: 'Prepare your application', resumable: true },
  'application-personal': { stage: 3, kicker: 'Your application', resumable: true },
  'application-health': { stage: 3, kicker: 'Your application', resumable: true },
  'application-medical': { stage: 3, kicker: 'Your application', resumable: true },
  'application-beneficiaries': { stage: 3, kicker: 'Your application', resumable: true },
  'application-review': { stage: 3, kicker: 'Review before sending', resumable: true },
  'your-plan': { stage: 3, kicker: 'Your plan', resumable: true },
} as const satisfies Record<string, { stage: number; kicker: string; resumable: boolean }>;

export type ConsumerStepId = keyof typeof CONSUMER_STEPS;

export const FIRST_CONSUMER_STEP: ConsumerStepId = 'setting';

/** The self-funding sequence, in order; every one needs active self-funding access. */
export const SELF_FUND_SEQUENCE_STEPS = [
  'assets',
  'assessment-intro',
  'assessment-accounts',
  'assessment-projection',
] as const satisfies readonly ConsumerStepId[];

export const FIRST_SELF_FUND_SEQUENCE_STEP: ConsumerStepId = 'assets';

export function isSelfFundSequenceStep(step: string | null | undefined): step is ConsumerStepId {
  return (SELF_FUND_SEQUENCE_STEPS as readonly string[]).includes(step ?? '');
}

/** The application screens; once the chosen option has been applied for they lead to Your Plan. */
export const APPLICATION_STEPS = [
  'application-start',
  'application-personal',
  'application-health',
  'application-medical',
  'application-beneficiaries',
  'application-review',
] as const satisfies readonly ConsumerStepId[];

export function isApplicationStep(step: string | null | undefined): step is ConsumerStepId {
  return (APPLICATION_STEPS as readonly string[]).includes(step ?? '');
}

const QUOTE_LIST_STEPS: readonly ConsumerStepId[] = ['options', 'compare'];

// Screens about the chosen quote: they wait for quotes, and without a matching choice the
// consumer is sent back to pick one.
const CHOSEN_QUOTE_STEPS: readonly ConsumerStepId[] = [
  'coverage',
  'policy-terms',
  'walkthrough',
  'walkthrough-summary',
  'review',
  ...APPLICATION_STEPS,
];

export function quotesNeededBy(step: ConsumerStepId): 'none' | 'quotes' | 'chosenQuote' {
  if (CHOSEN_QUOTE_STEPS.includes(step)) return 'chosenQuote';
  return QUOTE_LIST_STEPS.includes(step) ? 'quotes' : 'none';
}

export function isConsumerStep(value: string | null | undefined): value is ConsumerStepId {
  return !!value && value in CONSUMER_STEPS;
}

// Screens the flow no longer has, mapped to the screen that took over their job, so saved
// progress and old links still land somewhere sensible.
const RETIRED_STEPS: Record<string, ConsumerStepId> = {
  // Sending an application now finishes at Your Plan, which shows the application.
  submitted: 'your-plan',
  search: 'options',
  payment: 'options',
  matching: 'options',
  share: 'walkthrough-summary',
  family: 'walkthrough-summary',
  'family-hours': 'walkthrough-summary',
  'funding-start': 'walkthrough-summary',
  'funding-growth': 'walkthrough-summary',
  'funding-tax': 'walkthrough-summary',
  'funding-result': 'walkthrough-summary',
  'walk-choice': 'walkthrough-summary',
  'application-doctors': 'application-medical',
  'application-medications': 'application-medical',
  // Where care happens is now chosen on the scenario itself.
  'care-setting': 'scenario',
  'care-adjust': 'scenario',
  'care-age': 'scenario',
  'care-duration': 'scenario',
  'care-inflation': 'scenario',
};

export function currentConsumerStep(value: string | null | undefined): ConsumerStepId | null {
  if (isConsumerStep(value)) return value;
  return (value && RETIRED_STEPS[value]) || null;
}

export const APPLICATION_SECTION_STEPS = {
  personal: 'application-personal',
  health: 'application-health',
  medical: 'application-medical',
  beneficiaries: 'application-beneficiaries',
} as const satisfies Record<string, ConsumerStepId>;
